import React, { useEffect, useState } from "react";
import { FaCalendarAlt, FaListAlt, FaBook, FaStore, FaBroadcastTower } from "react-icons/fa";
import { FaTicket } from "react-icons/fa6";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { setActivePage } from "../store/activePage";
import HandleGetEventList from "../hooks/handleGetEventList";

const NAV_ITEMS = [
  {
    to: "/dashboard/moi-note",
    icon: FaBook,
    title: "Moi Note",
    desc: "Record and manage gift contributions for the event. Enter cash, online, ornaments, and item gifts.",
    accent: "#e11d48", /* Redesign: premium rose */
    glow: "rgba(225,29,72,0.06)",
    border: "rgba(225,29,72,0.25)",
    tag: "Contributions",
    featured: true,
  },
  {
    to: "/dashboard/create-token",
    icon: FaTicket,
    title: "Create Token",
    desc: "Generate a new contribution token for an event",
    accent: "#d97706", /* Redesign: premium gold */
    glow: "rgba(217,119,6,0.06)",
    border: "rgba(217,119,6,0.25)",
    tag: "Quick Action",
  },
  {
    to: "/dashboard/token-list",
    icon: FaListAlt,
    title: "Token History",
    desc: "View all your past tokens and their details",
    accent: "#059669", /* Redesign: premium green */
    glow: "rgba(5,150,105,0.06)",
    border: "rgba(5,150,105,0.25)",
    tag: "Records",
  },
  {
    to: "/dashboard/events-list",
    icon: FaCalendarAlt,
    title: "Events List",
    desc: "Browse and manage upcoming celebrations",
    accent: "#4f46e5", /* Redesign: premium indigo */
    glow: "rgba(79,70,229,0.06)",
    border: "rgba(79,70,229,0.25)",
    tag: "Calendar",
  },
  // {
  //   to: "/dashboard/find-vendors",
  //   icon: FaStore,
  //   title: "Find Enterprise",
  //   desc: "Search vendors by location and call them directly for event creation",
  //   accent: "#ea580c", /* Redesign: premium orange */
  //   glow: "rgba(234,88,12,0.06)",
  //   border: "rgba(234,88,12,0.25)",
  //   tag: "Event Setup",
  // },
  {
    to: "/dashboard/live-tracker",
    icon: FaBroadcastTower,
    title: "Live Tracker",
    desc: "Monitor entries and collection operator-wise in real-time.",
    accent: "#f59e0b", /* Redesign: premium amber */
    glow: "rgba(245,158,11,0.06)",
    border: "rgba(245,158,11,0.25)",
    tag: "Live Feed",
  },
];

function ActionCard({ item, index, hero }) {
  const [hovered, setHovered] = useState(false);
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        animationDelay: `${index * 110}ms`,
        animation: "cardRise 0.55s cubic-bezier(0.22,1,0.36,1) both",
        border: `1px solid ${hovered
            ? item.accent
            : "rgba(226,232,240,0.8)"
          }`,
        boxShadow: hovered
          ? `0 12px 28px -4px rgba(0,0,0,0.08), 0 0 16px ${item.glow}`
          : item.featured
            ? "0 4px 20px -4px rgba(0,0,0,0.05)"
            : "0 2px 8px -2px rgba(0,0,0,0.02)",
        transform: hovered
          ? "translateY(-4px) scale(1.008)"
          : "translateY(0) scale(1)",
        transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)",
      }}
      className={`relative flex overflow-hidden no-underline rounded-2xl bg-white ${hero
          ? "flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-8 p-6 sm:p-8"
          : "flex-row sm:flex-col items-center sm:items-start gap-4 sm:gap-4 p-4 sm:p-6"
        }`}
    >
      {/* Top shimmer line for featured */}
      {item.featured && (
        <div
          style={{
            background: `linear-gradient(90deg, transparent, ${item.accent}44, transparent)`,
          }}
          className="absolute top-0 left-0 w-full h-0.5 pointer-events-none"
        />
      )}

      {/* Icon */}
      <div
        style={{
          color: item.accent,
          filter: hovered
            ? `drop-shadow(0 0 8px ${item.accent}33)`
            : "none",
          transition: "filter 0.3s ease",
          fontSize: hero ? "3.5rem" : "1.75rem",
          flexShrink: 0,
        }}
        className="sm:text-[2.25rem]"
      >
        <Icon />
      </div>

      {/* Text block */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Tag + badge */}
        <div
          className={`flex items-center gap-2 ${hero ? "mb-3" : "mb-1.5 order-first"}`}
        >
          <span
            style={{
              color: item.accent,
              borderColor: item.border,
              background: item.glow,
            }}
            className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border"
          >
            {item.tag}
          </span>
          {item.featured && (
            <span className="text-[9px] font-bold text-rose-600 bg-rose-50 border border-rose-100 px-2 py-0.5 rounded-full">
              Primary
            </span>
          )}
        </div>

        <h2
          style={{ fontSize: hero ? "1.5rem" : undefined }}
          className="font-bold text-slate-800 tracking-tight leading-snug text-sm sm:text-[1.05rem] mb-1"
        >
          {item.title}
        </h2>
        <p
          className={`text-slate-500 leading-relaxed text-[11px] sm:text-xs ${hero ? "max-w-xl" : ""}`}
        >
          {item.desc}
        </p>

        {/* Arrow CTA — hidden on mobile for non-hero cards */}
        <div
          style={{
            color: item.accent,
            opacity: hovered || hero ? 1 : 0,
            transform: hovered ? "translateX(4px)" : "translateX(0)",
            transition: "all 0.25s ease",
          }}
          className={`hidden sm:flex items-center gap-1 font-bold ${hero ? "mt-4 text-sm" : "mt-3 text-[11px]"}`}
        >
          {hero ? "Open Moi Note" : "Open"}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </div>
      </div>

      {/* Chevron arrow on the right for mobile non-hero cards */}
      {!hero && (
        <div className="sm:hidden text-slate-350 shrink-0 ml-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      )}

      {/* Bottom accent line */}
      <div
        style={{
          background: `linear-gradient(90deg, ${item.accent}, transparent)`,
          opacity: hovered ? 1 : item.featured ? 0.45 : 0,
          transition: "opacity 0.3s ease",
        }}
        className="absolute bottom-0 left-0 w-full h-0.5 pointer-events-none"
      />
    </Link>
  );
}

function CustomerHome() {
  const dispatch = useDispatch();
  const [todayEvent, setTodayEvent] = useState(null);

  useEffect(() => {
    dispatch(setActivePage({ headerName: "Home page", activeMenu: "home" }));

    const checkTodayEvent = async () => {
      const eventList = await HandleGetEventList("");
      if (eventList && Array.isArray(eventList)) {
        const getLocalDateKey = (val) => {
          const date = val instanceof Date ? val : new Date(val);
          if (isNaN(date.getTime())) return "";
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        };
        const todayKey = getLocalDateKey(new Date());
        const match = eventList.find((event) => getLocalDateKey(event.event_date) === todayKey);
        if (match) {
          setTodayEvent(match);
        }
      }
    };
    checkTodayEvent();
  }, [dispatch]);

  const eventInfo = JSON.parse(localStorage.getItem("event-info") || "{}");
  const userName = localStorage.getItem("user-name") || null;

  const now = new Date();
  const greeting =
    now.getHours() < 12
      ? "Good morning"
      : now.getHours() < 17
        ? "Good afternoon"
        : "Good evening";

  const [heroItem, ...restItems] = NAV_ITEMS;

  return (
    <>
      <style>{`
        @keyframes cardRise {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .dot-grid {
          background-image: radial-gradient(circle, rgba(217,119,6,0.06) 1px, transparent 1px);
          background-size: 24px 24px;
        }
      `}</style>

      <div className="dot-grid min-h-full flex flex-col pb-12">
        {/* Greeting section */}
        <div
          style={{ animation: "fadeIn 0.5s ease both" }}
          className="px-4 sm:px-10 pt-6 sm:pt-10 pb-4 sm:pb-6 max-w-screen-lg mx-auto w-full"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-[10px] font-bold text-amber-700 uppercase tracking-widest bg-amber-50 px-2 py-0.5 rounded border border-amber-200/40">
              MOI NOTE &middot; Customer Portal
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-800 leading-tight tracking-tight">
            {greeting}
            {userName ? (
              <span className="text-amber-600">, {userName}</span>
            ) : (
              ""
            )}
          </h1>

          <p className="text-slate-500 mt-2 text-sm max-w-md leading-relaxed">
            {eventInfo?.event_name ? (
              <>
                You're registered for{" "}
                <span className="text-slate-800 font-bold">
                  {eventInfo.event_name}
                </span>
                . What would you like to do today?
              </>
            ) : (
              "Manage your event contributions, tokens, and registrations from one place."
            )}
          </p>

          <div className="mt-4 w-12 h-0.5 bg-gradient-to-r from-amber-500 to-transparent rounded-full" />
        </div>

        {/* Today's Live Event Banner */}
        {todayEvent && (
          <div
            style={{ animation: "fadeIn 0.5s 0.2s ease both" }}
            className="px-4 sm:px-10 pb-4 max-w-screen-lg mx-auto w-full"
          >
            <Link
              to="/dashboard/live-tracker"
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-2xl bg-gradient-to-r from-red-500/10 to-amber-500/10 border border-red-500/25 hover:border-red-500/50 hover:from-red-500/15 hover:to-amber-500/15 transition-all text-left group animate-[fadeIn_0.5s_ease]"
            >
              <div className="flex items-start sm:items-center gap-3">
                <span className="flex items-center justify-center shrink-0 w-8 h-8 rounded-xl bg-red-500 text-white text-sm animate-pulse">
                  📡
                </span>
                <div>
                  <h4 className="text-[10px] font-bold text-red-700 uppercase tracking-wider flex items-center gap-1.5">
                    Live Event Running Today
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-600 animate-ping" />
                  </h4>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">
                    {todayEvent.event_type} organized by {todayEvent.organisation_name}
                  </p>
                  <p className="text-xs text-slate-500 font-semibold mt-0.5">
                    Click to see live tracking of this today's event
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-red-650 font-bold text-xs shrink-0 self-end sm:self-center group-hover:translate-x-1 transition-transform">
                <span>View Live Tracker</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          </div>
        )}

        {/* Cards */}
        <div className="flex-1 px-4 sm:px-10 max-w-screen-lg mx-auto w-full space-y-4">
          {/* Moi Note — full-width hero card */}
          <ActionCard item={heroItem} index={0} hero={true} />

          {/* Remaining cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {restItems.map((item, i) => (
              <ActionCard
                key={item.to}
                item={item}
                index={i + 1}
                hero={false}
              />
            ))}
          </div>

          {/* Footer */}
          <p
            style={{ animation: "fadeIn 0.5s 0.65s ease both", opacity: 0 }}
            className="text-center text-[10px] text-slate-400 pt-6"
          >
            MOI NOTE &middot; Event Contribution Register &middot; All contributions are recorded securely
          </p>
        </div>
      </div>
    </>
  );
}

export default CustomerHome;
